import { FC, Fragment } from 'react';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Action, Button, Label, TableRow } from '../../atoms';
import { LandscapesResponseV3Dto } from '../../../store/landscapeAPI/landscape/dto/response/landscapes-response-v3.dto';
import { CountryCodeNames } from '../../../store/landscapeAPI/landscape/landscape-country-code-enum';
import ServerSortableTable from '../../molecules/ServerSortableTable';
import { SortConfig } from '../../../utils/types/types';
import landscapeServiceV3 from '../../../store/landscapeAPI/landscape/services/landspace.service-v3';
import { getQueryClient } from '../../../App';
import Spinner from '../../atoms/Spinner/Spinner';
import styles from './LandscapesOrganism.module.css';

export interface LandscapesOrganismProps {
  data: LandscapesResponseV3Dto['data'];
  sortConfig: SortConfig;
  handleLandscapeClick: (id: string) => void;
  setSortConfig: (value: SortConfig) => void;
}

const LandscapesOrganism: FC<LandscapesOrganismProps> = ({
  data,
  sortConfig,
  handleLandscapeClick,
  setSortConfig,
}) => {
  const navigate = useNavigate();
  const clientQuery = getQueryClient();

  const deleteLandscape = async (id: string) => {
    return await landscapeServiceV3.deleteLandscape(id);
  };

  const { mutate: handleDeleteLandscape, isPending: isPendingDeleteLandscape } =
    useMutation({
      mutationFn: deleteLandscape,
      onSuccess: () =>
        clientQuery.invalidateQueries({
          queryKey: ['landscape', 'landscapes'],
        }),
      onError: (error) => console.log(error),
    });

  if (isPendingDeleteLandscape) {
    return <Spinner />;
  }

  return (
    <ServerSortableTable
      data={data}
      sortConfig={sortConfig}
      setSortConfig={setSortConfig}
      columns={[
        { label: 'Name', key: 'name', sortKey: 'name' },
        { label: 'Date Created', key: 'createdDate', sortKey: 'datecreated' },
        { label: 'Country', key: 'country', sortKey: 'countrycode' },
        { label: 'Key User', key: 'keyUsers' },
        {
          label: 'Initiative Page Link',
          key: 'initiativeSlug',
        },
      ]}
      renderRow={(row) => (
        <TableRow key={row.initiativeId}>
          <Button
            className={cn(styles.buttonLink)}
            variant="link"
            onClick={() => handleLandscapeClick(row.initiativeId)}
          >
            {row.name}
          </Button>
          <Label
            content={dayjs(row.createdDate).format('D MMM YYYY').toString()}
          />
          <Label content={CountryCodeNames[row.country]} />
          <div className={styles.keyUsersWrapper}>
            {row.keyUsers?.map((keyUser, idx) => (
              <Fragment key={idx}>
                {idx !== 0 && <span>, </span>}
                <a
                  href={`/user-management/users/${keyUser.userId}/edit/details`}
                >
                  {keyUser.emailAddress}
                </a>
              </Fragment>
            ))}
          </div>
          {row.initiativeSlug && (
            <a
              href={`https://${process.env.REACT_APP_ENVIRONMENT !== 'prod' ? process.env.REACT_APP_ENVIRONMENT + `.` : ``}sourceup.org/initiatives/${row.initiativeSlug}`}
              target="_blank"
              rel="noreferrer"
            >
              {row.initiativeSlug}
            </a>
          )}
          <Action
            type="edit"
            size="xs"
            onClick={() =>
              navigate(`/landscapes/edit/${row.initiativeId}/basic-information`)
            }
          />
          <Action
            type="delete"
            size="xs"
            onClick={() => handleDeleteLandscape(row.initiativeId)}
          />
        </TableRow>
      )}
      showDeleteButtonHeader={true}
      showEditButtonHeader={true}
    />
  );
};

export default LandscapesOrganism;
